import { useApp } from '../../state/AppContext';
import Icon from '../../lib/Icon';
import { badge, zeb } from '../../lib/format';
import { cardPad, Th } from '../../lib/ui';

const ROLE_TH: Record<string, string> = {
  'Procurement': 'จัดซื้อ',
  'Director / CCO': 'ผู้อำนวยการ / CCO',
  'Warehouse': 'คลังสินค้า',
  'Compliance': 'กำกับดูแล',
  'Admin': 'ผู้ดูแลระบบ',
};
const DEPT_TH: Record<string, string> = {
  'Procurement': 'จัดซื้อ',
  'Executive': 'ผู้บริหาร',
  'Operations': 'ปฏิบัติการ',
  'Compliance': 'กำกับดูแล',
  'IT': 'IT',
};
const STATUS_TH: Record<string, string> = { 'Active': 'ใช้งาน', 'Suspended': 'ระงับ' };

export default function Users() {
  const { L } = useApp();

  const userRows = [
    { initial: 'MB', name: 'Maria Boonmee', email: 'maria.b@acme.co.th', role: 'Procurement', dept: 'Procurement', mfa: true, last: '2026-06-22 09:12', status: 'Active' },
    { initial: 'WA', name: 'Walter Adams', email: 'walter.a@acme.co.th', role: 'Director / CCO', dept: 'Executive', mfa: true, last: '2026-06-22 14:03', status: 'Active' },
    { initial: 'KP', name: 'Kris Panya', email: 'kris.p@acme.co.th', role: 'Warehouse', dept: 'Operations', mfa: false, last: '2026-06-21 16:40', status: 'Active' },
    { initial: 'WN', name: 'Wendy Nakamura', email: 'wendy.n@acme.co.th', role: 'Compliance', dept: 'Compliance', mfa: true, last: '2026-06-22 08:30', status: 'Active' },
    { initial: 'SS', name: 'Sanan Srisai', email: 'sanan.s@acme.co.th', role: 'Admin', dept: 'IT', mfa: true, last: '2026-06-20 11:55', status: 'Active' },
    { initial: 'PT', name: 'Ploy Thongchai', email: 'ploy.t@acme.co.th', role: 'Procurement', dept: 'Procurement', mfa: false, last: '2026-05-30 10:02', status: 'Suspended' },
  ].map((u, i) => {
    const b = badge(u.status);
    return {
      ...u,
      zebra: zeb(i), stBg: b.bg, stFg: b.fg,
      roleLabel: L(u.role, ROLE_TH[u.role] ?? u.role),
      deptLabel: L(u.dept, DEPT_TH[u.dept] ?? u.dept),
      statusLabel: L(u.status, STATUS_TH[u.status] ?? u.status),
      mfaColor: u.mfa ? 'var(--color-success)' : 'var(--color-warning)',
      mfaIcon: u.mfa ? 'shield-check' : 'shield-off',
      mfaText: u.mfa ? L('On', 'เปิด') : L('Off', 'ปิด'),
    };
  });
  const userFooter = L('6 users · 5 active, 1 suspended · MFA enforced for privileged roles', 'ผู้ใช้ 6 คน · ใช้งาน 5, ระงับ 1 · บังคับใช้ MFA สำหรับบทบาทที่มีสิทธิ์พิเศษ');

  return (
    <>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <Icon name="search" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-fg-3)', fontSize: 16 }} />
          <input placeholder={L('Search users…', 'ค้นหาผู้ใช้…')} style={{ width: '100%', padding: '9px 12px 9px 34px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-white)' }} />
        </div>
        <select style={{ padding: '9px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--color-white)' }}>
          <option>{L('All roles', 'บทบาททั้งหมด')}</option>
          <option>{L('Procurement Officer', 'เจ้าหน้าที่จัดซื้อ')}</option>
          <option>CCO</option>
          <option>{L('Director / Approver', 'ผู้อำนวยการ / ผู้อนุมัติ')}</option>
          <option>{L('Warehouse Staff', 'เจ้าหน้าที่คลังสินค้า')}</option>
        </select>
        <div style={{ flex: 1 }} />
        <button className="hv-primary" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: 'var(--color-primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600 }}>
          <Icon name="plus" />{L('Add User', 'เพิ่มผู้ใช้')}
        </button>
      </div>
      <div style={cardPad(0, 'auto')}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 900 }}>
          <thead>
            <tr style={{ background: 'var(--color-mist)', borderBottom: '1px solid var(--color-border)' }}>
              <Th>{L('User', 'ผู้ใช้')}</Th>
              <Th>{L('Email', 'อีเมล')}</Th>
              <Th>{L('Role', 'บทบาท')}</Th>
              <Th>{L('Department', 'แผนก')}</Th>
              <Th>MFA</Th>
              <Th>{L('Last Login', 'เข้าสู่ระบบล่าสุด')}</Th>
              <Th>{L('Status', 'สถานะ')}</Th>
              <Th align="right">{L('Actions', 'การดำเนินการ')}</Th>
            </tr>
          </thead>
          <tbody>
            {userRows.map((u) => (
              <tr key={u.email} style={{ borderBottom: '1px solid var(--color-border)', background: u.zebra }}>
                <td style={{ padding: '11px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 30, height: 30, borderRadius: 999, background: 'var(--color-blue)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{u.initial}</span>
                    <span style={{ fontWeight: 600 }}>{u.name}</span>
                  </div>
                </td>
                <td style={{ padding: '11px 16px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-fg-2)' }}>{u.email}</td>
                <td style={{ padding: '11px 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: 'var(--color-ink-50)', color: 'var(--color-fg-2)' }}>{u.roleLabel}</span>
                </td>
                <td style={{ padding: '11px 16px', color: 'var(--color-fg-2)' }}>{u.deptLabel}</td>
                <td style={{ padding: '11px 16px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: u.mfaColor }}>
                    <Icon name={u.mfaIcon} style={{ fontSize: 14 }} />{u.mfaText}
                  </span>
                </td>
                <td style={{ padding: '11px 16px', fontFamily: 'var(--font-mono)', color: 'var(--color-fg-2)' }}>{u.last}</td>
                <td style={{ padding: '11px 16px' }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 999, background: u.stBg, color: u.stFg }}>{u.statusLabel}</span>
                </td>
                <td style={{ padding: '11px 16px', textAlign: 'right' }}>
                  <button style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-blue)' }}>{L('Edit', 'แก้ไข')}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--color-fg-3)', borderTop: '1px solid var(--color-border)' }}>{userFooter}</div>
      </div>
    </>
  );
}
